/**
 * The AssignTraineePreferences-Component lets admins manually add members and change the status of existing members
 */
import { Box, Button, Divider, Paper, Typography, useTheme } from "@mui/material";
import React, { useContext, useEffect, useState } from "react";
import { AuthContext } from "../../context/auth-context/AuthContext";
import useDepartments from "../../hooks/members/useDepartments";
import useCurrentTraineeGeneration from "../../hooks/trainees/useCurrentTraineeGeneration";
import useTraineeGeneration from "../../hooks/trainees/useTraineeGeneration";
import { TraineeAssignment, TraineeChoiceDto } from "../../types/traineesTypes";
import api from "../../utils/api";
import { checkForPermission } from "../../utils/authUtils";
import { showErrorMessage } from "../../utils/toastUtils";
import TraineePreferencesTable from "../../components/members/trainees/assignTrainees/TraineePreferencesTable";
import MotivationalTextsDialog from "../../components/members/trainees/assignTrainees/MotivationalTextsDialog";
import ElectionDeadlineDialog from "../../components/members/trainees/assignTrainees/ElectionDeadlineDialog";
import dayjs, { Dayjs } from "dayjs";
import MentorsAccordion from "./MentorsAccordion";
import useMembers from "../../hooks/members/useMembers";
import { MentorDto } from "../../types/membersTypes";

/**
 * Options to create a new member and to change the status of members
 */
const AssignTrainees: React.FunctionComponent = () => {
  const theme = useTheme();

  /**
   * Function which proivdes the styles of the AssignTraineePreferences
   */
  const styles = {
    // Header text of a paper marking a section of a page
    paperHeaderText: {
      marginLeft: theme.spacing(1),
      marginTop: theme.spacing(1),
    },
    paperText: {
      marginLeft: theme.spacing(1),
      marginTop: theme.spacing(1),
    },
    // Header divider of a paper marking a section of a page
    paperHeaderDivider: {
      marginLeft: theme.spacing(1),
      marginRight: theme.spacing(1),
      marginTop: theme.spacing(2),
      marginBottom: theme.spacing(2),
    },
    paperContainer: {
      marginBottom: "10px",
    },
    inputContainer: {
      padding: theme.spacing(1),
    },
    selectionElement: {
      [theme.breakpoints.up("md")]: {
        margin: theme.spacing(1),
        width: "155px",
      },
      [theme.breakpoints.down("md")]: {
        margin: theme.spacing(1),
        width: "120px",
      },
      [theme.breakpoints.down("sm")]: {
        margin: theme.spacing(1),
        width: "120px",
      },
    },
    motivationalText: {
      marginLeft: theme.spacing(1),
      marginRight: theme.spacing(1),
    },
    inputButton: {
      margin: theme.spacing(1),
    },
    tableHeadSortBtn: {
      display: "flex",
      alignItems: "center",
    },
  };

  const { auth } = useContext(AuthContext);

  const [showMotivationalTexts, setShowMotivationalTexts] = useState<boolean>(false);
  const [openedTrainee, setOpenedTrainee] = useState<null | TraineeChoiceDto>(null);
  const [traineePreferences, setTraineePreferences] = useState<TraineeChoiceDto[]>([]);

  const [mentorToAdd, setMentorToAdd] = useState<MentorDto | null>(null);
  const [mentorToRemove, setMentorToRemove] = useState<MentorDto | null>(null);
  const [mentorOptions, setMentorOptions] = useState<MentorDto[]>([]);

  const [isElectionDeadlineDialogOpen, setIsElectionDeadlineDialogOpen] = useState<boolean>(false);
  const [electionStart, setElectionStart] = useState<Dayjs | null>(null);
  const [electionEnd, setElectionEnd] = useState<Dayjs | null>(null);

  const { currentTraineeGeneration } = useCurrentTraineeGeneration();

  const {
    internalProjectsOfGeneration,
    mentorsOfGeneration,
    traineePreferencesOfGeneration,
    isTraineePreferencesOfGenerationFetched,
    setElectionDeadline,
    addMentorToGeneration,
    removeMentorFromGeneration,
  } = useTraineeGeneration(currentTraineeGeneration?.generationId || 0);

  const { departments } = useDepartments();
  const filteredDepartments = departments.filter((department) => department.departmentId !== 8);

  const { members } = useMembers();

  // Set the mentor options for the mentor selection, filter out members who are not active or senior and members who are already mentors of the generation
  useEffect(() => {
    setMentorOptions(
      members
        // filter out members who are not active or senior
        .filter((member) => member.memberStatus.name === "aktives Mitglied" || member.memberStatus.name === "Senior")
        // filter out members who are already mentors of the generation
        .filter((member) => !mentorsOfGeneration.some((mentor) => mentor.memberId === member.memberId))
    );
  }, [members, mentorsOfGeneration]);

  useEffect(() => {
    if (isTraineePreferencesOfGenerationFetched) {
      setTraineePreferences(traineePreferencesOfGeneration);
    }
  }, [isTraineePreferencesOfGenerationFetched]);

  useEffect(() => {
    if (currentTraineeGeneration) {
      if (currentTraineeGeneration.electionStart) {
        setElectionStart(currentTraineeGeneration.electionStart ? dayjs(currentTraineeGeneration.electionStart) : null);
      }
      if (currentTraineeGeneration.electionEnd) {
        setElectionEnd(currentTraineeGeneration.electionEnd ? dayjs(currentTraineeGeneration.electionEnd) : null);
      }
    }
  }, [currentTraineeGeneration]);

  const closeMotivationalTexts = () => {
    setShowMotivationalTexts(false);
  };

  const openMotivationalTexts = (trainee: TraineeChoiceDto) => {
    if (trainee) {
      setOpenedTrainee(trainee);
      setShowMotivationalTexts(true);
    } else {
      showErrorMessage(
        "Die Motivationstexte konnten nicht gefunden werden, bitte laden Sie die Seite neu und probieren sie es erneut."
      );
    }
  };

  const savePreference = (traineeID: number, assignment: TraineeAssignment) => {
    api.patch(`/trainees/${traineeID}/assignment`, assignment).catch(() => {
      showErrorMessage("Die Zuteilung konnte nicht gespeichert werden, bitte lade die Seite neu.");
    });
  };

  const changeRessortChoice = (memberId: number, newValue: string) => {
    let id: number | null = null;
    if (newValue !== null) {
      id = parseInt(newValue);
    }
    setTraineePreferences((prevPreferences) => {
      const updatedPreferences = prevPreferences.map((preference) => {
        if (preference.memberId === memberId) {
          if (preference.departmentChoice === id) {
            const assignment: TraineeAssignment = {
              ipID: preference.internalProjectChoice,
              mentorID: preference.mentorChoice,
              departmentID: null,
            };
            savePreference(preference.memberId, assignment);
            return { ...preference, departmentChoice: null };
          } else {
            const assignment: TraineeAssignment = {
              ipID: preference.internalProjectChoice,
              mentorID: preference.mentorChoice,
              departmentID: id,
            };
            savePreference(preference.memberId, assignment);
            return { ...preference, departmentChoice: id };
          }
        } else {
          return preference;
        }
      });
      return updatedPreferences;
    });
  };

  const changeMentorChoice = (memberId: number, newValue: string) => {
    let id: number | null = null;
    if (newValue !== null) {
      id = parseInt(newValue);
    }
    setTraineePreferences((prevPreferences) => {
      const updatedPreferences = prevPreferences.map((preference) => {
        if (preference.memberId === memberId) {
          if (preference.mentorChoice === id) {
            const assignment: TraineeAssignment = {
              ipID: preference.internalProjectChoice,
              mentorID: null,
              departmentID: preference.departmentChoice,
            };
            savePreference(preference.memberId, assignment);
            return { ...preference, mentorChoice: null };
          } else {
            const assignment: TraineeAssignment = {
              ipID: preference.internalProjectChoice,
              mentorID: id,
              departmentID: preference.departmentChoice,
            };
            savePreference(preference.memberId, assignment);
            return { ...preference, mentorChoice: id };
          }
        } else {
          return preference;
        }
      });
      return updatedPreferences;
    });
  };

  const changeInternalProjectChoice = (memberId: number, newValue: string) => {
    let id: number | null = null;
    if (newValue !== null) {
      id = parseInt(newValue);
    }
    setTraineePreferences((prevPreferences) => {
      const updatedPreferences = prevPreferences.map((preference) => {
        if (preference.memberId === memberId) {
          if (preference.internalProjectChoice === id) {
            const assignment: TraineeAssignment = {
              ipID: null,
              mentorID: preference.mentorChoice,
              departmentID: preference.departmentChoice,
            };
            savePreference(preference.memberId, assignment);
            return { ...preference, internalProjectChoice: null };
          } else {
            const assignment: TraineeAssignment = {
              ipID: id,
              mentorID: preference.mentorChoice,
              departmentID: preference.departmentChoice,
            };
            savePreference(preference.memberId, assignment);
            return { ...preference, internalProjectChoice: id };
          }
        } else {
          return preference;
        }
      });
      return updatedPreferences;
    });
  };

  // Handles the saving of the election deadline, calls the setElectionDeadline function and closes the dialog
  const handleElectionDeadlineSave = () => {
    if (currentTraineeGeneration && electionStart && electionEnd) {
      setElectionDeadline(electionStart.toDate(), electionEnd.toDate());
    }
    setIsElectionDeadlineDialogOpen(false);
  };

  // Resets the election deadline dialog, sets the start and end date to null
  const resetElectionDeadlineDialog = () => {
    if (currentTraineeGeneration) {
      setElectionStart(currentTraineeGeneration.electionStart ? dayjs(currentTraineeGeneration.electionStart) : null);
      setElectionEnd(currentTraineeGeneration.electionEnd ? dayjs(currentTraineeGeneration.electionEnd) : null);
    }
    setIsElectionDeadlineDialogOpen(false);
  };

  // Handles the addition of a mentor to the generation, calls the addMentorToGeneration function and resets the mentorToAdd state
  const handleAddMentor = () => {
    if (mentorToAdd) {
      addMentorToGeneration(mentorToAdd.memberId);
      setMentorToAdd(null);
    }
  };

  // Handles the removal of a mentor from the generation, calls the removeMentorFromGeneration function and resets the mentorToRemove state
  const handleRemoveMentor = () => {
    if (mentorToRemove) {
      removeMentorFromGeneration(mentorToRemove.memberId);
      setMentorToRemove(null);
    }
  };

  const renderContent = () => {
    const isStartAndEndDateNull =
      currentTraineeGeneration?.electionStart === null && currentTraineeGeneration?.electionEnd === null;
    const hasPermission = checkForPermission(auth.permissions, [14]); // Permission ID 14 is required to view this page

    if (!isStartAndEndDateNull && hasPermission) {
      return (
        <Box sx={{ paddingBottom: 5 }}>
          <Paper sx={{ marginBottom: 2, padding: 2 }}>
            <Typography variant="h5" sx={styles.paperHeaderText}>
              Trainee-Zuteilung {currentTraineeGeneration?.description}
            </Typography>
            <Typography sx={styles.paperText}>
              Hier können die Präferenzen für die Wahl von Ressort, Mentor und Internem Projekt der Trainees zugeteilt
              werden.
              <br />
              Die Daten werden bei Änderung automatisch zwischengespeichert.
              <br />
              Sobald eine neue Traineegeneration angelegt wird, können die Zuteilungen nicht mehr geändert werden.
              <br />
              <br />
              Startzeit:{" "}
              <b>
                {currentTraineeGeneration && currentTraineeGeneration.electionStart
                  ? dayjs(currentTraineeGeneration.electionStart).format("DD.MM.YYYY HH:mm")
                  : ""}
              </b>
              <br />
              Deadline:{" "}
              <b>
                {currentTraineeGeneration && currentTraineeGeneration.electionEnd
                  ? dayjs(currentTraineeGeneration.electionEnd).format("DD.MM.YYYY HH:mm")
                  : ""}
              </b>
              <br />
            </Typography>
          </Paper>
          <Button
            variant="contained"
            color="primary"
            onClick={() => setIsElectionDeadlineDialogOpen(true)}
            sx={{ mb: 2 }}
          >
            Zeitraum ändern
          </Button>
          <MentorsAccordion
            mentors={mentorsOfGeneration}
            mentorOptions={mentorOptions}
            mentorToAdd={mentorToAdd}
            setMentorToAdd={setMentorToAdd}
            handleAddMentor={handleAddMentor}
            mentorToRemove={mentorToRemove}
            setMentorToRemove={setMentorToRemove}
            handleRemoveMentor={handleRemoveMentor}
          />
          <ElectionDeadlineDialog
            isElectionDeadlineDialogOpen={isElectionDeadlineDialogOpen}
            resetElectionDeadlineDialog={resetElectionDeadlineDialog}
            startDate={electionStart ? electionStart : null}
            endDate={electionEnd ? electionEnd : null}
            setElectionStart={setElectionStart}
            setElectionEnd={setElectionEnd}
            handleElectionDeadlineSave={handleElectionDeadlineSave}
          />
          <Divider sx={styles.paperHeaderDivider} />
          <TraineePreferencesTable
            traineePreferences={traineePreferences}
            changeRessortChoice={changeRessortChoice}
            filteredDepartments={filteredDepartments}
            changeMentorChoice={changeMentorChoice}
            mentorsOfGeneration={mentorsOfGeneration}
            changeInternalProjectChoice={changeInternalProjectChoice}
            internalProjectsOfGeneration={internalProjectsOfGeneration}
            openMotivationalTexts={openMotivationalTexts}
          />
        </Box>
      );
    }
    if (isStartAndEndDateNull) {
      return (
        <Paper>
          <ElectionDeadlineDialog
            isElectionDeadlineDialogOpen={isElectionDeadlineDialogOpen}
            resetElectionDeadlineDialog={resetElectionDeadlineDialog}
            startDate={electionStart ? electionStart : null}
            endDate={electionEnd ? electionEnd : null}
            setElectionStart={setElectionStart}
            setElectionEnd={setElectionEnd}
            handleElectionDeadlineSave={handleElectionDeadlineSave}
          />
          <Typography variant="h5" sx={styles.paperHeaderText}>
            Trainee-Zuteilung {currentTraineeGeneration?.description}
          </Typography>
          <Typography sx={styles.paperText}>
            Die Präferenzwahlen sind noch nicht geöffnet. Bitte gib einen Zeitraum für die Präferenzwahl an, um die
            Zuteilung der Trainees zu ermöglichen.
          </Typography>
          <Button
            variant="contained"
            color="primary"
            onClick={() => setIsElectionDeadlineDialogOpen(true)}
            sx={{ ml: 1, mt: 1, mb: 2 }}
          >
            Zeitraum festlegen
          </Button>
        </Paper>
      );
    }
    if (!hasPermission) {
      return (
        <Paper>
          <Typography variant="h5" sx={styles.paperHeaderText}>
            Trainee-Zuteilung {currentTraineeGeneration?.description}
          </Typography>
          <Typography sx={styles.paperText}>
            Du hast nicht die erforderlichen Berechtigungen, um diese Seite zu sehen.
          </Typography>
        </Paper>
      );
    }
    return null;
  };

  return (
    <div>
      {renderContent()}
      <MotivationalTextsDialog
        showMotivationalTexts={showMotivationalTexts}
        closeMotivationalTexts={closeMotivationalTexts}
        openedTrainee={openedTrainee}
      />
    </div>
  );
};

export default AssignTrainees;
